import React, { useState, useRef, useCallback, useContext, useEffect, useLayoutEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  DragIndicatorOutlined, AddOutlined, ImageOutlined, TimerOutlined, PeopleOutlined, EventNoteOutlined,
} from '@mui/icons-material';
import { PRIMARY, CONTAINER_COLOR, COUNTDOWN_COLOR, SPEAKERS_COLOR, ELEMENT_TYPES } from '../constants';
import { CtxMenuCtx, PreviewCtx, DragCtx, PageCtx } from '../contexts';
import { getEffectiveStyle, applyDragPreview, uid } from '../utils';
import { useDragEndReset } from '../hooks';
import { PageLinkPickerPopup } from './HeaderSection';
import { SpeakersElement } from './SpeakersElement';
import { ScheduleElement } from './ScheduleElement';

// ─── ElementPalette ───────────────────────────────────────────────────────────
function PaletteItem({ elKey, el, onDragStart }) {
  return (
    <Box draggable onDragStart={e => onDragStart(e, elKey)}
      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 2, border: `1px solid ${alpha(el.color, 0.2)}`, background: alpha(el.color, 0.04), cursor: 'grab', transition: 'all 0.15s', '&:hover': { background: alpha(el.color, 0.09), borderColor: alpha(el.color, 0.4), transform: 'translateX(2px)' }, '&:active': { cursor: 'grabbing' } }}>
      <Box sx={{ width: 34, height: 34, borderRadius: 1.5, background: alpha(el.color, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', color: el.color, flexShrink: 0 }}>{el.icon}</Box>
      <Box>
        <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#1a1a2e' }}>{el.label}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{el.sub}</Typography>
      </Box>
      <DragIndicatorOutlined sx={{ fontSize: 16, color: alpha('#000', 0.2), ml: 'auto' }} />
    </Box>
  );
}

function PaletteGroupLabel({ label }) {
  return (
    <Typography sx={{ fontWeight: 700, fontSize: '0.67rem', color: '#b0bac7', textTransform: 'uppercase', letterSpacing: '0.09em', pt: 0.5, pb: 0.25 }}>
      {label}
    </Typography>
  );
}

export function ElementPalette({ onDragStart }) {
  const baseEntries    = Object.entries(ELEMENT_TYPES).filter(([, el]) => !el.group);
  const specialEntries = Object.entries(ELEMENT_TYPES).filter(([, el]) =>  el.group);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}>
      <Box sx={{ px: 2, py: 2, borderBottom: '1px solid #e8edf2' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Elements</Typography>
        <Typography variant="caption" color="text.secondary">Drag onto the canvas</Typography>
      </Box>
      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {baseEntries.map(([key, el]) => (
          <PaletteItem key={key} elKey={key} el={el} onDragStart={onDragStart} />
        ))}

        {specialEntries.length > 0 && (
          <>
            <Box sx={{ borderTop: '1px solid #eef1f5', mt: 0.5, pt: 1 }}>
              <PaletteGroupLabel label="Interactive" />
            </Box>
            {specialEntries.map(([key, el]) => (
              <PaletteItem key={key} elKey={key} el={el} onDragStart={onDragStart} />
            ))}
          </>
        )}
      </Box>
    </Box>
  );
}

// ─── TextElement ──────────────────────────────────────────────────────────────
export function TextElement({ el, selected, onSelect, onChange, onDragStart, isDragOver }) {
  const [editing, setEditing] = useState(false);
  const ref = useRef();
  const { open: openMenu } = useContext(CtxMenuCtx);
  const bp = useContext(DragCtx)?.breakpointRef?.current || 'desktop';
  const cs = getEffectiveStyle(el, bp);

  const startEditing = () => {
    if (editing) return;
    setEditing(true);
    setTimeout(() => {
      const node = ref.current;
      if (!node) return;
      // Seed plain text so the content is visible and editable
      node.innerText = el.content || '';
      node.focus();
      // Place cursor at end
      const range = document.createRange();
      range.selectNodeContents(node);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }, 10);
  };

  return (
    <Box draggable={!editing} onDragStart={e => { const node = e.currentTarget; applyDragPreview(e, node); onDragStart(e); }}
      onClick={e => { e.stopPropagation(); onSelect(); }}
      onDoubleClick={e => { e.stopPropagation(); startEditing(); }}
      onContextMenu={e => { e.preventDefault(); e.stopPropagation(); onSelect(); openMenu(el.id, e.clientX, e.clientY); }}
      sx={{ display: 'block', position: 'relative', width: cs.width || undefined, alignSelf: cs.alignSelf || undefined, outline: isDragOver ? `2px dashed ${alpha(PRIMARY, 0.6)}` : selected ? `2px solid ${PRIMARY}` : '2px solid transparent', borderRadius: 0.5, cursor: editing ? 'text' : 'grab', '&:hover': { outline: selected || editing ? undefined : `2px dashed ${alpha(PRIMARY, 0.35)}` } }}>
      <Box ref={ref} contentEditable={editing} suppressContentEditableWarning
        onBlur={e => { setEditing(false); const val = e.target.innerText.trim(); onChange({ ...el, content: val || el.content || 'Click to edit text' }); }}
        onKeyDown={e => { if (e.key === 'Escape') { setEditing(false); ref.current?.blur(); } e.stopPropagation(); }}
        style={{ ...cs, outline: 'none', minWidth: '40px', minHeight: '20px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', display: 'block' }}
        dangerouslySetInnerHTML={editing ? undefined : { __html: el.content }}
      />
      {!editing && selected && (
        <Box sx={{ position: 'absolute', top: -22, left: 0, background: PRIMARY, color: '#fff', fontSize: '0.65rem', fontWeight: 700, px: 0.75, py: 0.25, borderRadius: '4px 4px 0 0', whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 100 }}>
          T · Double-click to edit
        </Box>
      )}
    </Box>
  );
}

// ─── ButtonElement ────────────────────────────────────────────────────────────
export function ButtonElement({ el, selected, onSelect, onChange, onDragStart, isDragOver }) {
  const [editingLabel, setEditingLabel] = useState(false);
  const [pickerOpen,   setPickerOpen]   = useState(false);
  const { open: openMenu } = useContext(CtxMenuCtx);
  const pageCtx = useContext(PageCtx);
  const bp = useContext(DragCtx)?.breakpointRef?.current || 'desktop';
  const cs = getEffectiveStyle(el, bp);
  const anchorRef = useRef(null);

  // Close picker when element is deselected
  useEffect(() => { if (!selected) setPickerOpen(false); }, [selected]);

  return (
    <Box
      draggable={!editingLabel}
      onDragStart={e => { const node = e.currentTarget; applyDragPreview(e, node); onDragStart(e); }}
      onClick={e => { e.stopPropagation(); onSelect(); setPickerOpen(v => !v); }}
      onContextMenu={e => { e.preventDefault(); e.stopPropagation(); onSelect(); openMenu(el.id, e.clientX, e.clientY); }}
      sx={{ display: 'inline-block', position: 'relative', width: cs.width || undefined, alignSelf: cs.alignSelf || undefined, outline: isDragOver ? `2px dashed ${alpha('#00a882', 0.6)}` : selected ? `2px solid ${PRIMARY}` : '2px solid transparent', borderRadius: 1, cursor: 'grab' }}
    >
      <Box ref={anchorRef}>
        {editingLabel ? (
          <input autoFocus defaultValue={el.label}
            onBlur={e => { setEditingLabel(false); onChange({ ...el, label: e.target.value }); }}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') e.target.blur(); e.stopPropagation(); }}
            onClick={e => e.stopPropagation()}
            style={{ ...cs, border: 'none', outline: `2px solid ${PRIMARY}`, cursor: 'text', fontFamily: 'inherit', width: Math.max(80, el.label.length * 9) + 'px' }} />
        ) : (
          <Box component="span"
            onDoubleClick={e => { e.stopPropagation(); setEditingLabel(true); setPickerOpen(false); }}
            style={{ ...cs, display: 'inline-block', cursor: 'grab' }}>
            {el.label}
          </Box>
        )}
      </Box>

      {selected && !editingLabel && (
        <Box sx={{ position: 'absolute', top: -22, left: 0, display: 'flex', gap: 0.5, zIndex: 100 }}>
          <Box sx={{ background: PRIMARY, color: '#fff', fontSize: '0.65rem', fontWeight: 700, px: 0.75, py: 0.25, borderRadius: '4px 4px 0 0', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
            Button · Dbl-click to rename
          </Box>
        </Box>
      )}

      {pickerOpen && selected && (
        <PageLinkPickerPopup
          anchorEl={anchorRef.current}
          allPages={pageCtx.allPages}
          allForms={pageCtx.allForms}
          allLobbies={pageCtx.allLobbies}
          allSections={pageCtx.allSections}
          currentHref={el.href}
          eventSlug={pageCtx.eventSlug}
          eventUrlMode={pageCtx.eventUrlMode}
          currentPageSlug={pageCtx.currentPageSlug}
          loginRedirectHref={pageCtx.loginRedirectHref}
          onLoginRedirectChange={pageCtx.onLoginRedirectChange}
          onSelect={item => onChange({ ...el, href: item.href })}
          onSelectExternal={url => onChange({ ...el, href: url })}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </Box>
  );
}

// ─── ImageElement ─────────────────────────────────────────────────────────────
export function ImageElement({ el, selected, onSelect, onDragStart, isDragOver }) {
  const IMG_COLOR = '#e67e22';
  const { open: openMenu } = useContext(CtxMenuCtx);
  const bp = useContext(DragCtx)?.breakpointRef?.current || 'desktop';
  const cs = getEffectiveStyle(el, bp);

  return (
    <Box draggable onDragStart={e => { const node = e.currentTarget; applyDragPreview(e, node); onDragStart(e); }}
      onClick={e => { e.stopPropagation(); onSelect(); }}
      onContextMenu={e => { e.preventDefault(); e.stopPropagation(); onSelect(); openMenu(el.id, e.clientX, e.clientY); }}
      sx={{ position: 'relative', width: cs.width || undefined, alignSelf: cs.alignSelf || undefined, outline: isDragOver ? `2px dashed ${alpha(IMG_COLOR, 0.6)}` : selected ? `2px solid ${IMG_COLOR}` : '2px solid transparent', borderRadius: 0.5, cursor: 'grab', '&:hover': { outline: selected ? undefined : `2px dashed ${alpha(IMG_COLOR, 0.35)}` } }}>
      {el.src ? (
        <img src={el.src} alt={el.alt || ''} style={{ ...cs, display: 'block' }} draggable={false} />
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, background: '#fff', borderRadius: '4px', minHeight: 120, width: '100%' }}>
          <ImageOutlined sx={{ fontSize: 40, color: '#c0c8d4' }} />
        </Box>
      )}
      {selected && (
        <Box sx={{ position: 'absolute', top: -22, left: 0, background: IMG_COLOR, color: '#fff', fontSize: '0.65rem', fontWeight: 700, px: 0.75, py: 0.25, borderRadius: '4px 4px 0 0', whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 100 }}>
          Image
        </Box>
      )}
    </Box>
  );
}


// ─── CountdownElement ─────────────────────────────────────────────────────────
export function CountdownElement({ el, selected, onSelect, onDragStart, isDragOver }) {
  const { open: openMenu } = useContext(CtxMenuCtx);
  const [timeLeft, setTimeLeft] = React.useState({});

  React.useEffect(() => {
    const calc = () => {
      const target = new Date(el.targetDate).getTime();
      const now    = Date.now();
      const diff   = Math.max(0, target - now);
      const days   = Math.floor(diff / 86400000);
      const hours  = Math.floor((diff % 86400000) / 3600000);
      const mins   = Math.floor((diff % 3600000)  / 60000);
      const secs   = Math.floor((diff % 60000)    / 1000);
      setTimeLeft({ days, hours, mins, secs });
    };
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, [el.targetDate]);

  const show   = el.show || { days: true, hours: true, mins: true, secs: true };
  const layout = el.layout || '2x2';

  const units = [
    { key: 'days',  label: 'Days',  value: timeLeft.days  ?? 0 },
    { key: 'hours', label: 'Hours', value: timeLeft.hours ?? 0 },
    { key: 'mins',  label: 'Mins',  value: timeLeft.mins  ?? 0 },
    { key: 'secs',  label: 'Sec',   value: timeLeft.secs  ?? 0 },
  ].filter(u => show[u.key]);

  const pad = n => String(n).padStart(2, '0');

  const CardItem = ({ unit }) => (
    <Box sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: el.cardBackground || '#f1f5f9',
      borderRadius: el.cardRadius || '12px',
      width: '100%',
      height: el.cardHeight || '100px',
    }}>
      <Typography sx={{ fontSize: el.numberSize || '48px', fontWeight: 700, color: el.numberColor || '#1a1a2e', lineHeight: 1 }}>
        {pad(unit.value)}
      </Typography>
      <Typography sx={{ fontSize: el.labelSize || '13px', color: el.labelColor || '#7a8494', mt: 0.5, fontWeight: 500 }}>
        {unit.label}
      </Typography>
    </Box>
  );

  return (
    <Box draggable onDragStart={e => { applyDragPreview(e, e.currentTarget); onDragStart(e); }}
      onClick={e => { e.stopPropagation(); onSelect(); }}
      onContextMenu={e => { e.preventDefault(); e.stopPropagation(); onSelect(); openMenu(el.id, e.clientX, e.clientY); }}
      sx={{ position: 'relative', outline: isDragOver ? `2px dashed ${alpha(COUNTDOWN_COLOR, 0.6)}` : selected ? `2px solid ${COUNTDOWN_COLOR}` : '2px solid transparent', borderRadius: 1, cursor: 'grab', '&:hover': { outline: selected ? undefined : `2px dashed ${alpha(COUNTDOWN_COLOR, 0.35)}` } }}>

      {layout === '2x2' ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', p: '4px' }}>
          {units.map(u => <CardItem key={u.key} unit={u} />)}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: '12px', p: '4px' }}>
          {units.map(u => <Box key={u.key} sx={{ flex: 1 }}><CardItem unit={u} /></Box>)}
        </Box>
      )}

      {selected && (
        <Box sx={{ position: 'absolute', top: -22, left: 0, background: COUNTDOWN_COLOR, color: '#fff', fontSize: '0.65rem', fontWeight: 700, px: 0.75, py: 0.25, borderRadius: '4px 4px 0 0', whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 100 }}>
          Countdown · {layout}
        </Box>
      )}
    </Box>
  );
}

// ─── AnyElement ───────────────────────────────────────────────────────────────
// Dispatcher — renders the right element type and handles visibility.
export function AnyElement({ el, selected, onSelect, onChange, onDragStart, isDragOver,
  selectedId, onSelectChild, onUpdateEl, onDeleteEl,
  onMoveChildToSection, onMoveChildToContainer, onInsertNewChildAt,
  onMoveTopLevelIntoContainer, onSplitDropIntoEmpty, sectionId,
}) {
  const isPreview = useContext(PreviewCtx);
  const bp = useContext(DragCtx)?.breakpointRef?.current || 'desktop';

  const vis = el.visibleOn || {};
  const isVisible = vis[bp] !== false;

  if (isPreview && !isVisible) return null;

  const noop = () => {};
  const baseProps = isPreview
    ? { el, selected: false, onSelect: noop, onChange: noop, onDragStart: noop, isDragOver: false }
    : { el, selected, onSelect, onChange, onDragStart, isDragOver };

  const rendered = (() => {
    if (el.type === 'text')   return <TextElement   {...baseProps} />;
    if (el.type === 'button') return <ButtonElement {...baseProps} />;
    if (el.type === 'image')  return <ImageElement  el={el} selected={isPreview ? false : selected} onSelect={isPreview ? noop : onSelect} onDragStart={isPreview ? noop : onDragStart} isDragOver={false} />;
    if (el.type === 'speakers')  return <SpeakersElement  el={el} selected={isPreview ? false : selected} onSelect={isPreview ? noop : onSelect} onDragStart={isPreview ? noop : onDragStart} isDragOver={false} />
    if (el.type === 'schedule')  return <ScheduleElement  el={el} selected={isPreview ? false : selected} onSelect={isPreview ? noop : onSelect} onDragStart={isPreview ? noop : onDragStart} />
    if (el.type === 'countdown') return <CountdownElement el={el} selected={isPreview ? false : selected} onSelect={isPreview ? noop : onSelect} onDragStart={isPreview ? noop : onDragStart} isDragOver={false} />;
    if (el.type === 'container') return (
      <ContainerElement
        el={el} selected={isPreview ? false : selected} onSelect={isPreview ? noop : onSelect} isDragOver={false}
        onDragStart={isPreview ? noop : onDragStart}
        selectedId={isPreview ? null : selectedId}
        onSelectChild={isPreview ? noop : onSelectChild}
        onUpdateEl={isPreview ? noop : onUpdateEl}
        onDeleteEl={isPreview ? noop : onDeleteEl}
        onMoveChildToSection={isPreview ? noop : onMoveChildToSection}
        onMoveChildToContainer={isPreview ? noop : onMoveChildToContainer}
        onInsertNewChildAt={isPreview ? noop : onInsertNewChildAt}
        onMoveTopLevelIntoContainer={isPreview ? noop : onMoveTopLevelIntoContainer}
        onSplitDropIntoEmpty={isPreview ? noop : onSplitDropIntoEmpty}
        sectionId={sectionId}
      />
    );
    return null;
  })();

  if (!isVisible) {
    return (
      <Box sx={{ position: 'relative', opacity: 0.35, pointerEvents: 'auto' }}>
        {rendered}
        <Box sx={{ position: 'absolute', top: 4, right: 4, background: '#e67e22', color: '#fff', fontSize: '0.6rem', fontWeight: 700, px: 0.75, py: 0.2, borderRadius: 1, pointerEvents: 'none', zIndex: 20, letterSpacing: '0.05em' }}>
          HIDDEN
        </Box>
      </Box>
    );
  }

  return rendered;
}

// ─── ContainerElement ─────────────────────────────────────────────────────────
export function ContainerElement({
  el, selected, onSelect, isDragOver, onDragStart,
  selectedId, onSelectChild,
  onUpdateEl, onDeleteEl,
  onMoveChildToSection, onMoveChildToContainer, onInsertNewChildAt,
  onMoveTopLevelIntoContainer, onSplitDropIntoEmpty,
  sectionId,
}) {
  const ctx          = useContext(DragCtx);
  const drag         = ctx.drag;
  const activeDropId = ctx.activeDropId;
  const bp           = ctx.breakpointRef?.current || 'desktop';
  const cs           = getEffectiveStyle(el, bp);
  const orientation  = cs.flexDirection || el.orientation || 'column';
  const isRow        = orientation === 'row';
  const isPreview    = useContext(PreviewCtx);
  const { open: openMenu } = useContext(CtxMenuCtx);
  const [overSlot,  setOverSlot]  = useState(null);
  const [splitSide, setSplitSide] = useState(null);
  const containerRef = useRef();
  const slotListRef  = useRef();

  useDragEndReset(useCallback(() => { setOverSlot(null); setSplitSide(null); }, []));

  const getSlot = useCallback((clientX, clientY) => {
    const root = slotListRef.current || containerRef.current;
    if (!root) return (el.children || []).length;
    const rows = Array.from(root.querySelectorAll(':scope > [data-child-slot]'));
    if (!rows.length) return 0;
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i].getBoundingClientRect();
      const mid = isRow ? r.left + r.width / 2 : r.top + r.height / 2;
      if ((isRow ? clientX : clientY) < mid) return i;
    }
    return rows.length;
  }, [(el.children || []).length, isRow]);

  const DropLine = ({ show }) => show ? (
    <Box sx={{ ...(isRow ? { width: 2, alignSelf: 'stretch', mx: 0.25 } : { height: 2, my: 0.25 }), background: CONTAINER_COLOR, borderRadius: 1, position: 'relative', zIndex: 10, boxShadow: `0 0 6px ${alpha(CONTAINER_COLOR, 0.5)}`, flexShrink: 0 }}>
      <Box sx={{ position: 'absolute', ...(isRow ? { top: -4, left: -3, width: 8, height: 8 } : { left: -4, top: -3, width: 8, height: 8 }), borderRadius: '50%', background: CONTAINER_COLOR }} />
    </Box>
  ) : null;

  const children = el.children || [];

  const handleDragOver = (e) => {
    const d = drag.current;
    if (!d) return;
    if (d.elId === el.id) return;
    e.preventDefault();
    e.stopPropagation();

    const rect = containerRef.current?.getBoundingClientRect();
    const hasOnlyLeafChildren = children.length > 0 && children.every(c => c.type !== 'container');

    if (rect) {
      const relX = e.clientX - rect.left;
      const zone = rect.width * 0.2;
      const inEdge = relX > rect.width - zone ? 'right' : null;
      if (inEdge && (children.length === 0 || (hasOnlyLeafChildren && !isRow))) {
        if (activeDropId.current !== el.id) ctx.resetAll();
        activeDropId.current = el.id;
        setSplitSide(inEdge);
        return;
      }
    }

    setSplitSide(null);
    if (children.length > 0) {
      if (activeDropId.current !== el.id) ctx.resetAll();
      activeDropId.current = el.id;
      setOverSlot(getSlot(e.clientX, e.clientY));
    }
  };

  const _makeSplitRow = (existingChildren, droppedEl, side) => {
    const colStyle = { padding: '12px', minHeight: '60px', background: 'transparent', borderRadius: '4px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'stretch', alignSelf: 'stretch' };
    const leftChildren  = side === 'left'  ? [droppedEl] : existingChildren;
    const rightChildren = side === 'right' ? [droppedEl] : existingChildren;
    return {
      id: uid(),
      ...JSON.parse(JSON.stringify(ELEMENT_TYPES.container.defaultData)),
      orientation: 'row',
      style: { ...JSON.parse(JSON.stringify(ELEMENT_TYPES.container.defaultData.style)), flexDirection: 'row', padding: cs?.padding || '16px', gap: '16px' },
      children: [
        { id: uid(), ...JSON.parse(JSON.stringify(ELEMENT_TYPES.container.defaultData)), style: colStyle, children: leftChildren },
        { id: uid(), ...JSON.parse(JSON.stringify(ELEMENT_TYPES.container.defaultData)), style: colStyle, children: rightChildren },
      ],
    };
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const d = drag.current;
    if (!d) return;
    if (d.elId === el.id) { drag.current = null; ctx.resetAll(); setOverSlot(null); setSplitSide(null); return; }

    const hasOnlyLeafChildren = children.length > 0 && children.every(c => c.type !== 'container');

    if (splitSide && (children.length === 0 || (hasOnlyLeafChildren && !isRow))) {
      if (d.kind === 'palette') {
        const droppedEl = { id: uid(), ...JSON.parse(JSON.stringify(ELEMENT_TYPES[d.elementType].defaultData)) };
        onUpdateEl(el.id, c => _makeSplitRow(c.children || [], droppedEl, splitSide));
      } else {
        onSplitDropIntoEmpty(el.id, d.elId, splitSide, children);
      }
      drag.current = null; ctx.resetAll(); setOverSlot(null); setSplitSide(null);
      return;
    }

    const slot = getSlot(e.clientX, e.clientY);
    if (d.kind === 'child' || d.kind === 'layer') {
      if (d.fromContainerId === el.id) {
        const to = slot > d.fromIdx ? slot - 1 : slot;
        if (d.fromIdx !== to) onUpdateEl(el.id, c => { const ch = [...(c.children || [])]; const [moved] = ch.splice(d.fromIdx, 1); ch.splice(to, 0, moved); return { ...c, children: ch }; });
      } else {
        onMoveChildToContainer(d.elId, d.fromContainerId, el.id, slot);
      }
    } else if (d.kind === 'topLevel') {
      onMoveTopLevelIntoContainer(d.elId, d.fromSectionId, el.id, slot);
    } else if (d.kind === 'palette') {
      onInsertNewChildAt(el.id, d.elementType, slot);
    }

    drag.current = null; ctx.resetAll(); setOverSlot(null); setSplitSide(null);
  };

  return (
    <Box
      draggable
      onDragStart={e => { e.stopPropagation(); applyDragPreview(e, e.currentTarget); onDragStart(e); }}
      onClick={e => { e.stopPropagation(); onSelect(); }}
      onContextMenu={e => { e.preventDefault(); e.stopPropagation(); onSelect(); openMenu(el.id, e.clientX, e.clientY); }}
      sx={{ position: 'relative', overflow: 'visible', outline: isDragOver ? `2px dashed ${alpha(CONTAINER_COLOR, 0.5)}` : selected ? `2px solid ${alpha(CONTAINER_COLOR, 0.8)}` : '2px solid transparent', borderRadius: 1, cursor: 'grab' }}
    >
      <Box ref={containerRef}
        style={(() => {
          const { overlayBackground, ...restStyle } = cs;
          const bgUrl = (cs.background || '').match(/url\(['"]?([^'"()]+)['"]?\)/)?.[1];
          const combined = overlayBackground && bgUrl
            ? { ...restStyle, backgroundImage: `${overlayBackground}, url('${bgUrl}')`, backgroundSize: cs.backgroundSize || 'cover', backgroundPosition: cs.backgroundPosition || 'center', backgroundRepeat: 'no-repeat' }
            : restStyle;
          return { ...combined, display: 'flex', flexDirection: 'column', overflow: 'visible', ...(!isPreview && { outline: '1px dotted #d0d7e2', outlineOffset: '0px' }) };
        })()}
        onDragOver={handleDragOver}
        onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) { setOverSlot(null); setSplitSide(null); if (activeDropId.current === el.id) activeDropId.current = null; } }}
        onDrop={handleDrop}
      >
        {children.length === 0 ? (
          isPreview ? null : drag.current ? (
            <Box sx={{ display: 'flex', minHeight: 200, borderRadius: 1, overflow: 'visible' }}>
              <Box sx={{ flex: 1, borderRadius: '6px 0 0 6px', border: `1.5px dashed ${splitSide === null ? alpha(PRIMARY, 0.5) : alpha(CONTAINER_COLOR, 0.2)}`, background: splitSide === null ? alpha(PRIMARY, 0.05) : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.1s' }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: splitSide === null ? PRIMARY : alpha(CONTAINER_COLOR, 0.4) }}>Drop here</Typography>
              </Box>
              <Box sx={{ width: '10%', flexShrink: 0, borderRadius: '0 6px 6px 0', border: `1.5px dashed ${splitSide === 'right' ? PRIMARY : alpha(CONTAINER_COLOR, 0.25)}`, background: splitSide === 'right' ? alpha(PRIMARY, 0.08) : alpha(CONTAINER_COLOR, 0.02), display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.1s' }}>
                <AddOutlined sx={{ fontSize: 18, color: splitSide === 'right' ? PRIMARY : alpha(CONTAINER_COLOR, 0.35) }} />
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: alpha(CONTAINER_COLOR, 0.45), fontSize: '0.82rem', border: `1.5px dashed ${alpha(CONTAINER_COLOR, 0.2)}`, borderRadius: 1, background: 'transparent', flex: 1 }}>
              Drop elements here
            </Box>
          )
        ) : (
          <Box sx={{ position: 'relative' }}>
            {drag.current && !isRow && children.every(c => c.type !== 'container') && (
              <Box sx={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: '10%', zIndex: 10, borderRadius: '0 6px 6px 0', pointerEvents: 'none', border: `1.5px dashed ${splitSide === 'right' ? PRIMARY : alpha(CONTAINER_COLOR, 0.25)}`, background: splitSide === 'right' ? alpha(PRIMARY, 0.08) : alpha(CONTAINER_COLOR, 0.02), display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.1s' }}>
                <AddOutlined sx={{ fontSize: 18, color: splitSide === 'right' ? PRIMARY : alpha(CONTAINER_COLOR, 0.35) }} />
              </Box>
            )}
            <Box ref={slotListRef}
              sx={{ display: 'flex', flexDirection: orientation, flexWrap: 'nowrap', overflow: 'visible', justifyContent: isRow ? (cs.alignItems || 'stretch') : (cs.justifyContent || 'flex-start'), alignItems: !isRow ? (cs.alignItems || 'stretch') : undefined, gap: cs.gap || '12px' }}>
              <DropLine show={overSlot === 0} />
              {children.map((child, idx) => (
                <React.Fragment key={child.id}>
                  <Box data-child-slot={idx} sx={{ flex: isRow ? '1 1 0' : undefined, minWidth: isRow ? 0 : undefined, display: 'flex', flexDirection: 'column', justifyContent: isRow ? (cs.justifyContent || 'flex-start') : undefined, overflow: 'visible' }}>
                    <AnyElement
                      el={child}
                      selected={selectedId === child.id}
                      onSelect={() => onSelectChild(child.id)}
                      onChange={updated => onUpdateEl(child.id, () => updated)}
                      onDragStart={e => {
                        e.stopPropagation();
                        applyDragPreview(e, e.currentTarget);
                        drag.current = { kind: 'child', elId: child.id, fromContainerId: el.id, fromSectionId: sectionId, fromIdx: idx };
                      }}
                      isDragOver={false}
                      selectedId={selectedId}
                      onSelectChild={onSelectChild}
                      onUpdateEl={onUpdateEl}
                      onDeleteEl={onDeleteEl}
                      onMoveChildToSection={onMoveChildToSection}
                      onMoveChildToContainer={onMoveChildToContainer}
                      onInsertNewChildAt={onInsertNewChildAt}
                      onMoveTopLevelIntoContainer={onMoveTopLevelIntoContainer}
                      onSplitDropIntoEmpty={onSplitDropIntoEmpty}
                      sectionId={sectionId}
                    />
                  </Box>
                  <DropLine show={overSlot === idx + 1} />
                </React.Fragment>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {selected && (
        <Box sx={{ position: 'absolute', top: -22, left: 0, background: CONTAINER_COLOR, color: '#fff', fontSize: '0.65rem', fontWeight: 700, px: 0.75, py: 0.25, borderRadius: '4px 4px 0 0', whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 100 }}>
          Container · {orientation}
        </Box>
      )}
    </Box>
  );
}